import React, { useEffect } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { closeModal } from '../../store/slices/uiSlice';
import Button from './Button';

interface ModalProps {
  id: string;
  title?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  onClose?: () => void;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  closeOnOverlayClick?: boolean;
}

const Modal: React.FC<ModalProps> = ({
  id,
  title,
  children,
  footer,
  onClose,
  size = 'md',
  closeOnOverlayClick = true,
}) => {
  const dispatch = useAppDispatch();
  const modal = useAppSelector((state) => state.ui.modals[id]);
  const isOpen = modal?.isOpen || false;

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  const handleClose = () => {
    dispatch(closeModal(id));
    onClose?.();
  };

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (closeOnOverlayClick && e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!isOpen) return null;

  const sizeClasses = {
    sm: 'max-w-md',
    md: 'max-w-lg',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
    full: 'max-w-full mx-4',
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? `${id}-title` : undefined}
    >
      {/* Overlay */}
      <div
        className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleOverlayClick}
        aria-hidden="true"
      />

      {/* Modal */}
      <div className="flex min-h-full items-center justify-center p-4">
        <div
          className={`
            relative bg-[var(--color-default-background)] rounded-lg shadow-xl w-full ${sizeClasses[size]}
            transform transition-all
          `}
          role="document"
        >
          {/* Header */}
          {title && (
            <div className="flex items-center justify-between p-6 border-b border-[var(--color-neutral-border)]">
              <h2 id={`${id}-title`} className="text-xl font-semibold text-[var(--color-default-font)]">
                {title}
              </h2>
              <button
                onClick={handleClose}
                className="text-[var(--color-neutral-400)] hover:text-[var(--color-neutral-600)] focus:outline-none focus:ring-2 focus:ring-[var(--color-brand-500)] rounded"
                aria-label="Close modal"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          )}

          {/* Content */}
          <div className="p-6 text-[var(--color-default-font)]">{children}</div>

          {/* Footer */}
          {footer && <div className="flex items-center justify-end gap-3 p-6 border-t border-[var(--color-neutral-border)]">{footer}</div>}
        </div>
      </div>
    </div>
  );
};

export default Modal;
